import { GetStateFunction, DispatchFunction } from "../reducers";
import {
    addTestAccount as addTestAccountApi,
    cloneUserToTestAccount as cloneUserToTestAccountApi,
    getTestAccounts as getTestAccountsApi,
    getUserDetailsByEmailOrUID,
    getUserObjectByUid,
    removeTestAccount as removeTestAccountApi,
    restoreTestAccount as restoreTestAccountApi,
    updateWorkoutStreak as updateWorkoutStreakApi,
    updateUser as updateUserApi,
} from "../api/users";
import { addError, addSuccess } from "./notifications";
import { USERS_LOADING, USERS_UPDATE } from "./types";
import { TestAccount, UserDetails, WorkoutStreak } from "../api/users.interface";


export const searchUserByEmailorUID = (search: string)  => async (dispatch: DispatchFunction): Promise<UserDetails | undefined> => {
    try {
        dispatch({ type: USERS_UPDATE, payload: { searchItem: undefined, loadingUsers: true } });
        const user = await getUserDetailsByEmailOrUID(search);

        dispatch({ type: USERS_UPDATE, payload: { searchItem: user, loadingUsers: false } });
        return user;
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while searching for the user.'));
        }
        
        dispatch({ type: USERS_LOADING, payload: false });
        return undefined;
    }
}

export const loadUserByUID = (uid: string) => async (dispatch: DispatchFunction, getState: GetStateFunction) => {
    dispatch({ type: USERS_LOADING, payload: true });
    try {
        const { user, stats } = await getUserObjectByUid(uid);

        dispatch({ type: USERS_UPDATE, payload: { user, stats, loadingUsers: false } });
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while loading the user. Please try again.'));
        }
        
        dispatch({ type: USERS_LOADING, payload: false });
    }
}

export const updateWorkoutStreak = (uid: string, workoutStreak: WorkoutStreak) => async (dispatch: DispatchFunction, getState: GetStateFunction) => {
    const { user } = getState().users;
    if (!user) {
        dispatch(addError('User not found.'));
        return
    }
    const userUpdate = {
        ...user,
        goals: {
            ...user.goals,
            workoutStreak: {
                ...workoutStreak,
                loggedAt: Date.now()
            }
        }
    }
    dispatch({ type: USERS_UPDATE, payload: { user: userUpdate } });
    try {
        await updateWorkoutStreakApi(uid, workoutStreak);
        dispatch(addSuccess('Workout streak updated successfully.'))
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while updating the workout streak. Please try again.'));
        }
        dispatch({ type: USERS_UPDATE, payload: { user } });
    }
}

export const updateUser = (uid: string, update: Partial<UserDetails>) => async (dispatch: DispatchFunction, getState: GetStateFunction) => {
    const { user } = getState().users;
    if (!user) {
        dispatch(addError('User not found.'));
        return
    }
    const userUpdate = {
        ...user,
        ...update,
    }
    dispatch({ type: USERS_UPDATE, payload: { user: userUpdate } });
    try {
        await updateUserApi(uid, update);
        dispatch(addSuccess('User updated successfully.'))
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while updating this user. Please try again.'));
        }
        dispatch({ type: USERS_UPDATE, payload: { user } });
    }
}

export const loadTestAccounts = () => async (dispatch: DispatchFunction) => {
    dispatch({ type: USERS_UPDATE, payload: { loadingTestAccounts: true } });
    try {
        const testAccounts = await getTestAccountsApi();
        dispatch({
            type: USERS_UPDATE,
            payload: { loadingTestAccounts: false, testAccounts },
        });
    } catch {
        dispatch(addError('An error occurred while loading test accounts.'));
        dispatch({ type: USERS_UPDATE, payload: { loadingTestAccounts: false } });
    }
}

export const addTestAccount = (testAccount: TestAccount) => async (
    dispatch: DispatchFunction,
    getState: GetStateFunction,
): Promise<boolean> => {
    try {
        await addTestAccountApi(testAccount.uid);
        const testAccounts = await getTestAccountsApi();
        dispatch({ type: USERS_UPDATE, payload: { testAccounts } });
        dispatch(addSuccess('Test account added successfully.'));
        return true;
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while adding the test account.'));
        }
        return false;
    }
}

export const removeTestAccount = (uid: string) => async (
    dispatch: DispatchFunction,
    getState: GetStateFunction,
): Promise<boolean> => {
    try {
        await removeTestAccountApi(uid);
        const currentTestAccounts = getState().users.testAccounts || [];
        dispatch({ type: USERS_UPDATE, payload: { testAccounts: currentTestAccounts.filter((item) => item.uid !== uid) } });
        dispatch(addSuccess('Test account removed successfully.'));
        return true;
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while removing the test account.'));
        }
        return false;
    }
}

export const restoreTestAccount = (uid: string) => async (
    dispatch: DispatchFunction,
): Promise<boolean> => {
    try {
        await restoreTestAccountApi(uid);
        const testAccounts = await getTestAccountsApi();
        dispatch({ type: USERS_UPDATE, payload: { testAccounts } });
        dispatch(addSuccess('Test account restored successfully.'));
        return true;
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while restoring the test account.'));
        }
        return false;
    }
}

export const cloneUserToTestAccount = (uid: string, targetUid: string) => async (
    dispatch: DispatchFunction,
): Promise<boolean> => {
    try {
        await cloneUserToTestAccountApi(uid, targetUid);
        const testAccounts = await getTestAccountsApi();
        dispatch({ type: USERS_UPDATE, payload: { testAccounts } });
        dispatch(addSuccess('User cloned into the test account successfully.'));
        return true;
    } catch (e: any) {
        if (e.userFriendly) {
            dispatch(addError(e.message));
        } else {
            dispatch(addError('An error occurred while cloning the user into the test account.'));
        }
        return false;
    }
}
